// apps/prompts/src/hooks/usePrompts.ts
import { ApiError, searchPrompts, useAuthStore } from '@neuroforge/shared/client'
import { toast } from '@neuroforge/ui'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { authPrincipalKey } from '../lib/auth-query-policy'

import {
  fetchPrompts,
  fetchPromptBySlug,
  trackPromptCopy,
  toggleFavoriteApi,
  fetchFavorites,
  fetchRelatedPrompts,
  fetchSearchSuggestions,
  fetchUserStats,
  type PromptListItem,
  type PromptDetail,
} from '@/lib/api-prompts'

type PromptSearchFallbackResult = {
  hits: PromptListItem[]
  estimatedTotalHits: number
  offset: number
  limit: number
  fallback: 'rest'
}

// ─── Keys factory ───
const promptKeys = {
  all: ['prompts'] as const,
  lists: () => [...promptKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...promptKeys.lists(), filters] as const,
  details: () => [...promptKeys.all, 'detail'] as const,
  detail: (slug: string) => [...promptKeys.details(), slug] as const,
  favorites: () => [...promptKeys.all, 'favorites'] as const,
  search: (query: string, filters: Record<string, unknown>) =>
    [...promptKeys.all, 'search', query, filters] as const,
  stats: () => [...promptKeys.all, 'stats'] as const,
}

// ─── Liste paginée (API REST) ───
export function usePromptList(
  params: {
    category?: string
    aiModel?: string
    tag?: string
    page?: number
    limit?: number
    sort?: 'recent' | 'popular' | 'order'
  } = {},
) {
  return useQuery({
    queryKey: promptKeys.list(params),
    queryFn: () => fetchPrompts(params),
    staleTime: 2 * 60 * 1000, // 2 min
  })
}

// ─── Recherche MeiliSearch ───
export function usePromptSearch(
  query: string,
  filters: {
    category?: string
    aiModel?: string
    tag?: string
    sort?: string
  } = {},
) {
  const meiliFilters: string[] = []
  if (filters.category) meiliFilters.push(`category = "${filters.category}"`)
  if (filters.aiModel) meiliFilters.push(`aiModel = "${filters.aiModel}"`)
  if (filters.tag) meiliFilters.push(`tags = "${filters.tag}"`)

  return useQuery({
    queryKey: promptKeys.search(query, filters),
    queryFn: async () => {
      const sort =
        filters.sort === 'popular'
          ? ['copyCount:desc']
          : filters.sort === 'recent'
            ? ['createdAt:desc']
            : undefined

      try {
        return await searchPrompts(query, {
          filter: meiliFilters.length > 0 ? meiliFilters.join(' AND ') : undefined,
          sort,
          limit: 100,
          facets: ['category', 'aiModel', 'tags'],
        })
      } catch (error) {
        console.warn('[usePromptSearch] MeiliSearch failed, falling back to REST:', error)
        const restSort =
          filters.sort === 'popular' ? 'popular' : filters.sort === 'recent' ? 'recent' : 'order'
        const fallback = await fetchPrompts({
          category: filters.category,
          aiModel: filters.aiModel,
          sort: restSort,
          limit: 100,
        })

        return {
          hits: fallback.data,
          estimatedTotalHits: fallback.meta.total,
          offset: 0,
          limit: fallback.meta.limit,
          fallback: 'rest',
        } satisfies PromptSearchFallbackResult
      }
    },
    enabled: query.trim().length > 0,
    staleTime: 30 * 1000, // 30s
  })
}

// ─── Détail prompt par id ou slug ───
// La clé inclut isAuthenticated : le serveur tronque le content pour les
// anonymes, il faut donc refetcher après login/logout (sinon le cache 5 min
// continuerait de servir le teaser à un utilisateur connecté).
export function usePromptDetail(idOrSlug: string | undefined) {
  const { isAuthenticated, accessToken } = useAuthStore()
  const queryClient = useQueryClient()
  const principal = authPrincipalKey(useAuthStore.getState())

  return useQuery({
    queryKey: [...promptKeys.detail(idOrSlug ?? ''), { isAuthenticated }],
    queryFn: async () => {
      const detail = await fetchPromptBySlug(idOrSlug!)

      // La route legacy /prompts/:slug redirige vers /prompts/:id/:slug.
      // Eviter un deuxième GET pour le même détail après cette redirection :
      // en particulier, une deuxième requête peut être limitée par le throttler.
      // Le cache canonique conserve la même audience et n'est jamais alimenté
      // par une réponse d'une session qui a changé pendant le premier GET.
      const currentAuth = useAuthStore.getState()
      if (
        detail.id &&
        detail.id !== idOrSlug &&
        currentAuth.isAuthenticated === isAuthenticated &&
        currentAuth.accessToken === accessToken &&
        authPrincipalKey(currentAuth) === principal
      ) {
        queryClient.setQueryData<PromptDetail>(
          [...promptKeys.detail(detail.id), { isAuthenticated }],
          (cached) => cached ?? detail,
        )
      }

      return detail
    },
    enabled: !!idOrSlug,
    staleTime: 5 * 60 * 1000, // 5 min
    // Ne jamais retenter sur une erreur 4xx : retenter un 404 est inutile et
    // retenter un 429 aggrave le rate limiting (le retry x3 par défaut de
    // React Query amplifiait le déclenchement du throttler).
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false
      return failureCount < 2
    },
  })
}

// ─── Top prompts (populaires) ───
// Retourne { prompts, isLoading, isError } directement exploitable.
// fetchPrompts renvoie déjà la réponse unwrappée { data, meta } (PROMPTS-API-CLIENT-01).
export function useTopPrompts(limit = 6) {
  const query = useQuery({
    queryKey: promptKeys.list({ sort: 'popular', limit }),
    queryFn: () => fetchPrompts({ sort: 'popular', limit }),
    staleTime: 5 * 60 * 1000,
  })

  if (query.error) {
    console.error('[useTopPrompts] fetchPrompts error:', query.error)
  }

  const prompts: PromptListItem[] = query.data?.data ?? []

  return {
    prompts,
    isLoading: query.isLoading,
    isError: query.isError,
  }
}

// ─── Favoris ───
export function useFavorites() {
  const { isAuthenticated } = useAuthStore()
  const queryClient = useQueryClient()

  const favoritesQuery = useQuery({
    queryKey: promptKeys.favorites(),
    queryFn: () => fetchFavorites(1, 200),
    enabled: isAuthenticated,
    staleTime: 60 * 1000, // 1 min
  })

  const favData = Array.isArray(favoritesQuery.data?.data) ? favoritesQuery.data.data : []
  const favoriteIds = new Set(favData.map((f) => f.id))

  const toggleMutation = useMutation({
    mutationFn: toggleFavoriteApi,
    onMutate: async (_promptId: string) => {
      const principal = authPrincipalKey(useAuthStore.getState())
      // Optimistic update : si la session change pendant le cancel, ne pas
      // capturer le cache du nouveau compte sous une mutation de l'ancien.
      await queryClient.cancelQueries({ queryKey: promptKeys.favorites() })
      if (principal !== authPrincipalKey(useAuthStore.getState())) {
        return { prev: undefined, principal }
      }
      const prev = queryClient.getQueryData(promptKeys.favorites())
      return { prev, principal }
    },
    onSuccess: (result, _promptId, context) => {
      if (!context || context.principal !== authPrincipalKey(useAuthStore.getState())) return
      if (result.favorited) {
        toast({ type: 'success', message: 'Ajouté aux favoris ⭐' })
      } else {
        toast({ type: 'info', message: 'Retiré des favoris' })
      }
    },
    onSettled: (_data, _err, _promptId, context) => {
      if (!context || context.principal !== authPrincipalKey(useAuthStore.getState())) return
      queryClient.invalidateQueries({ queryKey: promptKeys.favorites() })
      queryClient.invalidateQueries({ queryKey: promptKeys.lists() })
    },
    onError: (_err, _promptId, context) => {
      if (!context || context.principal !== authPrincipalKey(useAuthStore.getState())) return
      if (context.prev) {
        queryClient.setQueryData(promptKeys.favorites(), context.prev)
      }
      toast({ type: 'error', message: 'Erreur lors de la mise à jour du favori' })
    },
  })

  return {
    favorites: favData,
    favoriteIds,
    isFavorite: (id: string) => favoriteIds.has(id),
    toggleFavorite: (id: string) => {
      if (!isAuthenticated) {
        toast({ type: 'info', message: 'Connecte-toi pour sauvegarder tes favoris' })
        return
      }
      toggleMutation.mutate(id)
    },
    isLoading: favoritesQuery.isLoading,
    isToggling: toggleMutation.isPending,
  }
}

// ─── Copy tracking ───
export function useCopyPrompt() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ promptId, content }: { promptId: string; content: string }) => {
      // Copier dans le clipboard
      await navigator.clipboard.writeText(content)
      // Track côté API (fire-and-forget)
      return trackPromptCopy(promptId)
    },
    onSuccess: () => {
      toast({ type: 'success', message: 'Prompt copié dans le presse-papier ! 📋' })
    },
    onError: () => {
      toast({ type: 'error', message: 'Erreur lors de la copie' })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: promptKeys.lists() })
    },
  })
}

// ─── Stats globales ───
// fetchPrompts renvoie déjà { data, meta } unwrappé (PROMPTS-API-CLIENT-01).
export function usePromptStats() {
  const allPrompts = useQuery({
    queryKey: promptKeys.stats(),
    queryFn: () => fetchPrompts({ limit: 200, sort: 'order' }),
    staleTime: 5 * 60 * 1000,
  })

  const raw = allPrompts.data
  const prompts = Array.isArray(raw?.data) ? raw.data : []
  const total = raw?.meta?.total ?? prompts.length
  const categories = new Set(prompts.map((p: { category: string }) => p.category))

  return {
    total,
    categoryCount: categories.size,
    prompts, // pour sidebar counts
    isLoading: allPrompts.isLoading,
  }
}

// ─── Batch K: Related + Suggestions + User Stats ───

export function useRelatedPrompts(promptId: string | undefined, limit = 6) {
  return useQuery({
    queryKey: [...promptKeys.all, 'related', promptId],
    queryFn: () => fetchRelatedPrompts(promptId!, limit),
    enabled: !!promptId,
    staleTime: 10 * 60 * 1000, // 10 min — relations changent rarement
  })
}

export function useSearchSuggestions(query: string, limit = 6) {
  return useQuery({
    queryKey: [...promptKeys.all, 'suggest', query],
    queryFn: () => fetchSearchSuggestions(query, limit),
    enabled: query.trim().length >= 2,
    staleTime: 30 * 1000, // 30s — suggestions légères
  })
}

export function useUserStats() {
  const { isAuthenticated } = useAuthStore()
  return useQuery({
    queryKey: [...promptKeys.all, 'me', 'stats'],
    queryFn: fetchUserStats,
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 min
  })
}
